import { clsx } from 'clsx';
import type React from 'react';
import type { MouseEvent, Ref } from 'react';
import Stack from '../../_components/Stack';
import { slowCn } from '../../lib/utils';
import { Box } from '../Box';
import type { TooltipProps } from '../Tooltip';
import { Typography } from '../Typography';
import { ListItem } from './Components/ListItem';

export enum MenuItemHelpTextPosition {
  Inline = 'inline',
  InTooltip = 'tooltip',
}

export type MenuContentProps = {
  startDecorator?: React.ReactNode;
  endDecorator?: React.ReactNode;
  title: string;
  titleClassName?: string;
  menuContentContainerClassName?: string;
  helpTextDetails?: {
    text: string | React.ReactNode;
    position: MenuItemHelpTextPosition;
    placement?: TooltipProps['placement'];
  };
  helpTextClassName?: string;
  caption?: string;
  size?: 'sm' | 'md';
  onClick?: (event: MouseEvent) => void;
  onMouseOver?: (event: MouseEvent) => void;
  onMouseEnter?: (event: MouseEvent) => void;
  disabled?: boolean;
  selected?: boolean;
  className?: string;
  onMouseDown?: (event: MouseEvent) => void;
  index?: number;
  id?: string;
  ref?: Ref<HTMLLIElement>;
};

function MenuContent({
  startDecorator,
  endDecorator,
  title,
  titleClassName,
  helpTextDetails,
  helpTextClassName,
  menuContentContainerClassName,
  caption,
  size,
  onClick,
  onMouseOver,
  onMouseEnter,
  disabled,
  selected,
  className,
  onMouseDown,
  index,
  id,
  ref,
}: MenuContentProps) {
  return (
    <ListItem
      aria-label={title}
      className={slowCn(
        'row bg-primary my-xxs mx-sm ps-md pe-lg flex items-center rounded-sm is-platform:rounded-md',
        { 'cursor-pointer': onClick },
        { 'pointer-events-none': disabled },
        { 'bg-quaternary': selected },
        { 'py-md !h-9 is-platform:!h-8': size === 'md' },
        { 'py-sm !h-7 !min-h-7': size === 'sm' },
        className,
      )}
      data-content-type={id}
      data-menu-index={index}
      data-selected={selected}
      id={id}
      onClick={onClick}
      onMouseDown={onMouseDown}
      onMouseEnter={onMouseEnter}
      onMouseOver={onMouseOver}
      ref={ref}
    >
      <Stack className="gap-xxs w-full">
        <Stack className="w-full" direction="row">
          <Box
            className={slowCn(
              'row gap-x-sm flex me-auto flex-row items-center is-platform:gap-x-md',
              menuContentContainerClassName,
            )}
          >
            {startDecorator}
            <Typography
              className={titleClassName}
              maxNumberOfLines={1}
              variant={size === 'sm' ? 'text-xs' : 'text-sm'}
            >
              {title}
            </Typography>
          </Box>
          {helpTextDetails?.position === MenuItemHelpTextPosition.Inline ? (
            <Typography className={helpTextClassName} variant="text-xs" weight="regular">
              {helpTextDetails.text}
            </Typography>
          ) : null}
          {endDecorator}
        </Stack>
        {caption ? (
          <Stack className="w-full" direction="row">
            <Typography className="text-secondary mb-xxs" variant="text-xs" weight="regular">
              {caption}
            </Typography>
          </Stack>
        ) : null}
      </Stack>
    </ListItem>
  );
}

export default MenuContent;
